import { type ComponentChildren, createContext, type h } from "preact";
import { useContext, useRef } from "preact/hooks";
import type { Option } from "../lib/data.ts";
import { shell } from "../lib/native.ts";
import { useId } from "../lib/uid.ts";
import { ChooserIcon } from "./icons.tsx";
import { MenuPopover } from "./Menu.tsx";
import { CONTROL, CONTROL_SM } from "./styles.ts";

interface FieldIds {
  id: string;
  describedBy?: string;
  invalid: boolean;
}

interface FieldProps {
  label: ComponentChildren;
  hint?: ComponentChildren;
  error?: ComponentChildren | null;
  /** Several controls (a list, amount + currency): a labelled fieldset instead of one labelled control. */
  group?: boolean;
  /** Its id, for links straight to it (`#theme`: the omnibar's settings). */
  anchor?: string;
  /** Its label for screen readers only: what's in it shows its name its own way (a heading with its tools). */
  labelHidden?: boolean;
  children: ComponentChildren;
}

type Handler<T extends EventTarget> = (value: string, e: h.JSX.TargetedEvent<T>) => void;

type InputProps = Omit<h.JSX.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> & {
  value: string;
  /** Smaller, for secondary rows. */
  compact?: boolean;
  onValue?: Handler<HTMLInputElement>;
};

type TextareaProps = Omit<h.JSX.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value"> & {
  value: string;
  onValue: Handler<HTMLTextAreaElement>;
};

type SelectProps = Omit<h.JSX.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value"> & {
  value: string;
  /** Smaller, for secondary rows. */
  compact?: boolean;
  options: readonly Option[];
  onValue: Handler<HTMLSelectElement>;
};

type CheckboxProps = Omit<h.JSX.InputHTMLAttributes<HTMLInputElement>, "onChange" | "checked" | "label"> & {
  checked: boolean;
  label: ComponentChildren;
  onCheck: (checked: boolean) => void;
};

const FieldContext = createContext<FieldIds | null>(null);

function Note(p: { id: string; error: boolean; text: ComponentChildren }) {
  return (
    <p id={p.id} class={`text-xs ${p.error ? "text-red-700" : "text-zinc-500"}`}>
      {p.text}
    </p>
  );
}

/** id / aria wiring for the control inside a <Field>. */
function useFieldProps() {
  const f = useContext(FieldContext);
  return f ? { id: f.id, "aria-describedby": f.describedBy, "aria-invalid": f.invalid || undefined } : {};
}

/** Consecutive options by their group (undefined: ungrouped). */
function groups(options: readonly Option[]): [string | undefined, Option[]][] {
  const out: [string | undefined, Option[]][] = [];
  for (const o of options) {
    const last = out.at(-1);
    if (last && last[0] === o.group) {
      last[1].push(o);
    } else {
      out.push([o.group, [o]]);
    }
  }
  return out;
}

/** Label + control + hint/error. The control inside picks up its id and aria wiring from context. */
export function Field(p: FieldProps) {
  const id = useId();
  const note = p.error ?? p.hint;
  const ids: FieldIds = { id, invalid: Boolean(p.error), ...(note ? { describedBy: `${id}-note` } : {}) };
  const noteEl = note ? <Note id={`${id}-note`} error={Boolean(p.error)} text={note} /> : null;
  if (p.group) {
    return (
      <fieldset id={p.anchor} aria-describedby={ids.describedBy} class="flex min-w-0 flex-col gap-1.5 scroll-m-8">
        <legend class={p.labelHidden ? "sr-only" : "mb-1.5 text-sm font-medium text-zinc-800"}>{p.label}</legend>
        <FieldContext.Provider value={null}>{p.children}</FieldContext.Provider>
        {noteEl}
      </fieldset>
    );
  }
  return (
    <div id={p.anchor} class="flex scroll-m-8 flex-col gap-1.5">
      <label for={id} class="text-sm font-medium text-zinc-800">
        {p.label}
      </label>
      <FieldContext.Provider value={ids}>{p.children}</FieldContext.Provider>
      {noteEl}
    </div>
  );
}

export function Input({ value, onValue, compact = false, class: cls, ...rest }: InputProps) {
  return (
    <input
      {...useFieldProps()}
      {...rest}
      class={`${compact ? `${CONTROL_SM} h-7 ios:h-8 android:h-9` : `${CONTROL} h-9 ios:h-11 android:h-12`} ${cls ?? ""}`}
      value={value}
      onInput={(e) => onValue?.(e.currentTarget.value, e)}
    />
  );
}

export function Textarea({ value, onValue, class: cls, ...rest }: TextareaProps) {
  return (
    <textarea
      {...useFieldProps()}
      rows={3}
      {...rest}
      class={`${CONTROL} ${cls ?? ""}`}
      value={value}
      onInput={(e) => onValue(e.currentTarget.value, e)}
    />
  );
}

/**
 * A select whose choices show more than their names (an entity's icon): a field's control opening a menu, as the
 * app's menus (the browser's own select shows text only).
 */
export function MenuSelect(p: {
  value: string;
  options: readonly { value: string; label: string; icon?: ComponentChildren }[];
  onValue: (v: string) => void;
}) {
  const field = useFieldProps();
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const picked = p.options.find((o) => o.value === p.value);
  return (
    <>
      <button
        ref={trigger}
        type="button"
        {...field}
        popovertarget={id}
        aria-haspopup="menu"
        class={`${CONTROL} flex h-9 items-center gap-2 text-left disabled:opacity-60 ios:h-11 android:h-12`}
      >
        {picked?.icon}
        <span class="min-w-0 flex-1 truncate">{picked?.label}</span>
        <ChooserIcon class="shrink-0 text-zinc-500" />
      </button>
      <MenuPopover
        id={id}
        trigger={trigger}
        matchWidth={true}
        items={p.options.map((o) => ({
          key: o.value,
          label: o.label,
          icon: o.icon,
          current: o.value === p.value,
          onSelect: () => p.onValue(o.value),
        }))}
      />
    </>
  );
}

export function Select({ value, options, onValue, compact = false, class: cls, ...rest }: SelectProps) {
  return (
    <select
      {...useFieldProps()}
      {...rest}
      class={`${compact ? `${CONTROL_SM} h-7 pr-7 ios:h-8 android:h-9` : `${CONTROL} h-9 pr-9 ios:h-11 android:h-12`} select-chevron ${cls ?? ""}`}
      value={value}
      onChange={(e) => onValue(e.currentTarget.value, e)}
    >
      {groups(options).map(([group, list]) => {
        const items = list.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ));
        return group ? (
          <optgroup key={group} label={group}>
            {items}
          </optgroup>
        ) : (
          items
        );
      })}
    </select>
  );
}

/** One of `options` as radio buttons (each with an optional hint under it); `label` names the group. */
export function Radios<T extends string>(p: {
  label: string;
  /** None yet: nothing checked. */
  value: T | undefined;
  options: readonly { value: T; label: string; hint?: string }[];
  onValue: (v: T) => void;
  /** Small print, packed close: a choice inside a list's row. */
  small?: boolean;
}) {
  const name = useId();
  return (
    <fieldset class={`flex flex-col ${p.small ? "gap-1.5" : "gap-3"}`}>
      <legend class="sr-only">{p.label}</legend>
      {p.options.map((o) => (
        <label key={o.value} class={`flex items-start text-zinc-800 ${p.small ? "gap-1.5 text-xs" : "gap-2 text-sm"}`}>
          <input
            type="radio"
            name={name}
            class={`shrink-0 accent-blue-600 ${p.small ? "mt-px size-3.5" : "mt-0.5 size-4"}`}
            checked={o.value === p.value}
            onChange={() => p.onValue(o.value)}
          />
          <span>
            <span class="block">{o.label}</span>
            {o.hint ? <span class="block text-zinc-500">{o.hint}</span> : null}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

export function Checkbox({ checked, label, onCheck, ...rest }: CheckboxProps) {
  return (
    <label class="inline-flex items-center gap-2 text-sm text-zinc-800">
      <input
        {...rest}
        type="checkbox"
        class="size-4 rounded border-zinc-300 accent-blue-600"
        checked={checked}
        onChange={(e) => onCheck(e.currentTarget.checked)}
      />
      {label}
    </label>
  );
}

/** Whether WebKit draws the system's switch (`<input type="checkbox" switch>`). */
const SYSTEM_SWITCH = typeof HTMLInputElement !== "undefined" && "switch" in HTMLInputElement.prototype;

/**
 * An on/off setting that takes effect at once (a checkbox, instead, is a choice that waits for its form to be saved).
 * Still a checkbox underneath, announced as a switch; in the iOS app, the system's own switch (Safari's `switch`).
 */
export function Switch({ checked, label, onCheck, class: cls, ...rest }: CheckboxProps) {
  // The system's switch where WebKit draws one; else ours, as iOS's.
  const system = shell()?.platform === "ios" && SYSTEM_SWITCH;
  return (
    <label
      class={`inline-flex cursor-pointer items-center gap-2 text-sm text-zinc-800 has-disabled:cursor-not-allowed has-disabled:opacity-50 ${cls ?? ""}`}
    >
      <input
        {...rest}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        // A property too (Preact sets it as one): true, not the attribute's "" (false as a property).
        {...(system ? ({ switch: true } as Record<string, boolean>) : {})}
        class={system ? "shrink-0" : "peer sr-only"}
        checked={checked}
        onChange={(e) => onCheck(e.currentTarget.checked)}
      />
      {system ? null : (
        <span
          aria-hidden="true"
          class="relative h-5 w-9 shrink-0 rounded-full bg-zinc-300 transition-colors after:absolute after:top-0.5 after:left-0.5 after:size-4 after:rounded-full after:bg-on-fill after:shadow-sm after:transition-transform peer-checked:bg-primary peer-checked:after:translate-x-4 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-blue-600 ios:h-[31px] ios:w-[51px] ios:bg-zinc-950/15 ios:after:top-[2px] ios:after:left-[2px] ios:after:size-[27px] ios:after:shadow-md ios:peer-checked:bg-primary ios:peer-checked:after:translate-x-5 android:h-8 android:w-[52px] android:border-2 android:border-zinc-500 android:bg-zinc-200 android:after:top-[6px] android:after:left-[6px] android:after:bg-zinc-500 android:after:shadow-none android:after:transition-all android:after:duration-(--md-spatial-duration) android:after:ease-(--md-spatial) android:peer-checked:border-primary android:peer-checked:bg-primary android:peer-checked:after:top-[2px] android:peer-checked:after:size-6 android:peer-checked:after:translate-x-4 android:peer-checked:after:bg-on-fill"
        />
      )}
      {label}
    </label>
  );
}

type FormProps = Omit<h.JSX.HTMLAttributes<HTMLFormElement>, "onSubmit"> & {
  /** Submitted (the browser's own submission, a page load, kept from happening). */
  onSubmit: () => void;
  children: ComponentChildren;
};

/** A form that submits by calling `onSubmit` (Enter in a field, or its submit button). */
export function Form({ onSubmit, children, ...rest }: FormProps) {
  return (
    <form
      {...rest}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {children}
    </form>
  );
}
